// Prominent disclosure (PMU-238; Google Play's user data policy): before the phone asks for the
// location or the microphone, say what is collected, who sees it, when, and when it stops, and
// let the person agree or decline. Remembered on this phone once agreed; shown again if the text
// changes (the version) or the app's data is cleared.
import { useRef, useState } from "react";
import { Button, Dialog, DialogClose } from "@vavr/ui";
import "./safety.css";

export interface DisclosureText { key: string; version: number; title: string; points: string[]; agree: string }

export const LOCATION_DISCLOSURE: DisclosureText = {
  key: "location", version: 1, title: "Share the bus's location", agree: "Agree and start",
  points: [
    "While a run is active, Vavr collects this phone's precise location, including when the app is closed or the screen is off.",
    "The school, the bus company and the parents of the children on this route see where the bus is and when it will arrive. Nobody sees it outside a run.",
    "Sharing stops when you finish the run, or when you tap Stop sharing. A notification shows while it is on.",
    "Locations are kept with the run for the school's records and deleted on its schedule.",
  ],
};
export const MIC_DISCLOSURE: DisclosureText = {
  key: "microphone", version: 1, title: "The staff radio uses the microphone", agree: "Agree and turn on",
  points: [
    "The radio records your voice only while you hold the talk button, and sends it live to colleagues on the radio at your school.",
    "It keeps listening for colleagues while the radio is on, including with the screen off. A notification shows while it is on.",
    "Turn it off at any time with Turn off, or from the notification.",
  ],
};

const seen = (d: DisclosureText) => { try { return localStorage.getItem(`pmu.disclosed.${d.key}`) === String(d.version); } catch { return false; } };

/** `ask()` resolves true once the person agreed (now or before); render `dialog` somewhere on the screen. */
export function useDisclosure(d: DisclosureText): { ask: () => Promise<boolean>; dialog: React.ReactNode } {
  const [open, setOpen] = useState(false);
  const pending = useRef<((ok: boolean) => void) | null>(null);
  const finish = (ok: boolean) => {
    if (ok) try { localStorage.setItem(`pmu.disclosed.${d.key}`, String(d.version)); } catch { /* shown again next time */ }
    pending.current?.(ok); pending.current = null; setOpen(false);
  };
  const ask = () => seen(d) ? Promise.resolve(true) : new Promise<boolean>((res) => { pending.current = res; setOpen(true); });
  const dialog = (
    <Dialog title={d.title} open={open} onOpenChange={(v) => { if (!v) finish(false); }}
      actions={<><DialogClose><Button variant="secondary">Not now</Button></DialogClose><Button onClick={() => finish(true)}>{d.agree}</Button></>}>
      <ul className="psf-disclose">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>
    </Dialog>
  );
  return { ask, dialog };
}
